import { graphql } from 'react-relay';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';

import WebItemLocationQuery, {
	type WebItemLocationQuery$data,
	type WebItemLocationQuery$variables,
} from './__generated__/WebItemLocationQuery.graphql.ts';

import type {
	preloadWebItemLocationWithSpaceKeyQuery$data,
	preloadWebItemLocationWithSpaceKeyQuery$variables,
} from './__generated__/preloadWebItemLocationWithSpaceKeyQuery.graphql.ts';

const preloadWebItemLocationWithSpaceKeyQuery = graphql`
	query preloadWebItemLocationWithSpaceKeyQuery(
		$location: String
		$locations: [String]
		$spaceKey: String
		$version: Int
	) {
		space(key: $spaceKey) {
			id
			homepageId
			homepageWebSections(location: $location, locations: $locations, version: $version) {
				id
				label
				cacheKey
				styleClass
				items {
					accessKey
					completeKey
					hasCondition
					id
					icon {
						height
						width
						path
					}
					params {
						key
						value
					}
					label
					moduleKey
					section
					styleClass
					tooltip
					url
					urlWithoutContextPath
					weight
				}
			}
		}
	}
`;

type PreloadWebItemLocationContext = {
	single?: boolean;
};

export function preloadWebItemLocation({
	location = null,
	contentId = null,
	spaceKey = null,
	version = null,
	context,
}: {
	location?: string | string[] | null;
	contentId?: string | null;
	spaceKey?: string | null;
	version?: number | null;
	context?: PreloadWebItemLocationContext;
} = {}) {
	if (!contentId && spaceKey) {
		return query<
			preloadWebItemLocationWithSpaceKeyQuery$data,
			preloadWebItemLocationWithSpaceKeyQuery$variables
		>({
			query: preloadWebItemLocationWithSpaceKeyQuery,
			variables: {
				location: Array.isArray(location) ? null : location,
				locations: Array.isArray(location) ? location : [],
				spaceKey,
				version,
			},
			context,
		}).then(({ data }) => {
			if (data?.space?.homepageId) {
				writeQueryInterop<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
					query: WebItemLocationQuery,
					variables: {
						location: Array.isArray(location) ? null : location,
						locations: Array.isArray(location) ? location : [],
						contentId: data?.space?.homepageId,
						spaceKey: null,
						version,
					},
					data: {
						webItemSections: data?.space?.homepageWebSections || [],
					},
				});
			}
		});
	}
	const variables = {
		// GraphQL doesn't cache single location strings properly when passed to locations
		// unless we're using multiple locations we should be using the location variable
		location: Array.isArray(location) ? null : location,
		locations: Array.isArray(location) ? location : [],
		contentId,
		spaceKey,
		version,
	};

	return query<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
		query: WebItemLocationQuery,
		variables,
		context,
	});
}
